import { useId, useState } from 'react';

/**
 * Gráficos da Tesouraria (ADR 0084, D-10): SVG próprios, sem biblioteca. Cores pela paleta
 * categórica validada (`--chart-1..4`, sempre nesta ordem); texto na cor do texto; uma escala só;
 * legenda a partir de duas séries; dica ao passar o mouse; e a tabela ao lado na tela (os mesmos
 * valores). Valor ausente nunca vira zero: o ponto some da linha.
 */

export interface Series {
  name: string;
  values: (number | null)[];
}

const COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)'];
const W = 720;
const H = 240;
const PAD = { top: 12, right: 12, bottom: 28, left: 64 };

function scale(values: (number | null)[]) {
  const nums = values.filter((v): v is number => v !== null && Number.isFinite(v));
  let min = Math.min(0, ...nums);
  let max = Math.max(0, ...nums);
  if (min === max) max = min + 1;
  const pad = (max - min) * 0.08;
  min -= min < 0 ? pad : 0;
  max += pad;
  const y = (v: number) => PAD.top + ((max - v) / (max - min)) * (H - PAD.top - PAD.bottom);
  const ticks = [min, (min + max) / 2, max].map((v) => ({ v, y: y(v) }));
  return { y, ticks, zero: y(0) };
}

const short = (v: number, format: (v: number) => string) => format(v);

function Legend({ series }: { series: Series[] }) {
  if (series.length < 2) return null;
  return (
    <ul className="flex flex-wrap gap-4 text-xs text-muted">
      {series.map((s, i) => (
        <li key={s.name} className="flex items-center gap-1">
          <span
            aria-hidden
            className="inline-block h-2 w-3 rounded-sm"
            style={{ background: COLORS[i % COLORS.length] }}
          />
          {s.name}
        </li>
      ))}
    </ul>
  );
}

/** Linhas no tempo (evolução do caixa, cenários), com a marca do dia de referência. */
export function LineChart({
  title,
  labels,
  series,
  marker,
  format,
}: {
  title: string;
  labels: string[];
  series: Series[];
  /** Índice do dia de referência: realizado à esquerda, projetado à direita. */
  marker?: { index: number; label: string };
  format: (v: number) => string;
}) {
  const id = useId();
  const [hover, setHover] = useState<number | null>(null);
  const { y, ticks, zero } = scale(series.flatMap((s) => s.values));
  const n = Math.max(labels.length, 1);
  const x = (i: number) => PAD.left + (n === 1 ? 0 : (i / (n - 1)) * (W - PAD.left - PAD.right));
  const path = (values: (number | null)[]) => {
    let d = '';
    let pen = false;
    values.forEach((v, i) => {
      if (v === null) {
        pen = false;
        return;
      }
      d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
      pen = true;
    });
    return d;
  };
  return (
    <figure className="flex flex-col gap-2" aria-labelledby={`${id}-t`}>
      <figcaption id={`${id}-t`} className="text-sm font-medium">
        {title}
      </figcaption>
      <Legend series={series} />
      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="h-auto w-full"
          role="img"
          aria-label={title}
          onMouseLeave={() => setHover(null)}
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const px = ((e.clientX - r.left) / r.width) * W;
            const i = Math.round(((px - PAD.left) / (W - PAD.left - PAD.right)) * (n - 1));
            setHover(Math.max(0, Math.min(n - 1, i)));
          }}
        >
          {ticks.map((t) => (
            <g key={t.v}>
              <line x1={PAD.left} x2={W - PAD.right} y1={t.y} y2={t.y} stroke="var(--rule)" />
              <text x={PAD.left - 6} y={t.y + 4} textAnchor="end" fontSize="11" fill="var(--muted)">
                {short(t.v, format)}
              </text>
            </g>
          ))}
          <line x1={PAD.left} x2={W - PAD.right} y1={zero} y2={zero} stroke="var(--muted)" />
          {marker && (
            <g>
              <line
                x1={x(marker.index)}
                x2={x(marker.index)}
                y1={PAD.top}
                y2={H - PAD.bottom}
                stroke="var(--muted)"
                strokeDasharray="4 4"
              />
              <text x={x(marker.index) + 4} y={PAD.top + 10} fontSize="11" fill="var(--muted)">
                {marker.label}
              </text>
            </g>
          )}
          {series.map((s, i) => (
            <path
              key={s.name}
              d={path(s.values)}
              fill="none"
              stroke={COLORS[i % COLORS.length]}
              strokeWidth={2}
              strokeLinejoin="round"
            />
          ))}
          {[0, n - 1].map((i) => (
            <text
              key={i}
              x={x(i)}
              y={H - 8}
              fontSize="11"
              fill="var(--muted)"
              textAnchor={i === 0 ? 'start' : 'end'}
            >
              {labels[i]}
            </text>
          ))}
          {hover !== null && (
            <g>
              <line
                x1={x(hover)}
                x2={x(hover)}
                y1={PAD.top}
                y2={H - PAD.bottom}
                stroke="var(--ink)"
                strokeOpacity={0.3}
              />
              {series.map((s, i) =>
                s.values[hover] === null || s.values[hover] === undefined ? null : (
                  <circle
                    key={s.name}
                    cx={x(hover)}
                    cy={y(s.values[hover]!)}
                    r={4}
                    fill={COLORS[i % COLORS.length]}
                    stroke="var(--sheet)"
                    strokeWidth={2}
                  />
                ),
              )}
            </g>
          )}
        </svg>
        {hover !== null && (
          <div
            role="tooltip"
            className="pointer-events-none absolute top-2 rounded border border-rule bg-sheet px-2 py-1 text-xs shadow"
            style={{ left: `${Math.min(70, (x(hover) / W) * 100)}%` }}
          >
            <div className="font-medium">{labels[hover]}</div>
            {series.map((s) => (
              <div key={s.name}>
                {s.name}:{' '}
                {s.values[hover] === null || s.values[hover] === undefined
                  ? '—'
                  : format(s.values[hover]!)}
              </div>
            ))}
          </div>
        )}
      </div>
    </figure>
  );
}

/**
 * Barras agrupadas (entradas × saídas, orçado × previsto × realizado) ou empilhadas (composição
 * das saídas). 2px de folga entre as barras; ponta arredondada.
 */
export function BarChart({
  title,
  labels,
  series,
  stacked = false,
  format,
}: {
  title: string;
  labels: string[];
  series: Series[];
  stacked?: boolean;
  format: (v: number) => string;
}) {
  const id = useId();
  const [hover, setHover] = useState<number | null>(null);
  const totals = stacked
    ? labels.map((_, i) => series.reduce((acc, s) => acc + Math.max(0, s.values[i] ?? 0), 0))
    : series.flatMap((s) => s.values);
  const { y, ticks, zero } = scale(totals);
  const n = Math.max(labels.length, 1);
  const band = (W - PAD.left - PAD.right) / n;
  const inner = Math.max(4, band * 0.7);
  const barW = stacked ? inner : Math.max(2, inner / series.length - 2);
  return (
    <figure className="flex flex-col gap-2" aria-labelledby={`${id}-t`}>
      <figcaption id={`${id}-t`} className="text-sm font-medium">
        {title}
      </figcaption>
      <Legend series={series} />
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label={title}>
          {ticks.map((t) => (
            <g key={t.v}>
              <line x1={PAD.left} x2={W - PAD.right} y1={t.y} y2={t.y} stroke="var(--rule)" />
              <text x={PAD.left - 6} y={t.y + 4} textAnchor="end" fontSize="11" fill="var(--muted)">
                {format(t.v)}
              </text>
            </g>
          ))}
          {labels.map((label, i) => {
            const x0 = PAD.left + i * band + (band - inner) / 2;
            let acc = 0;
            return (
              <g key={label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                <rect
                  x={PAD.left + i * band}
                  y={PAD.top}
                  width={band}
                  height={H - PAD.top - PAD.bottom}
                  fill="transparent"
                />
                {series.map((s, k) => {
                  const v = s.values[i];
                  if (v === null || v === undefined) return null;
                  const base = stacked ? acc : 0;
                  if (stacked) acc += Math.max(0, v);
                  const top = y(stacked ? base + Math.max(0, v) : Math.max(0, v));
                  const bottom = y(stacked ? base : Math.min(0, v));
                  return (
                    <rect
                      key={s.name}
                      x={stacked ? x0 : x0 + k * (barW + 2)}
                      y={Math.min(top, bottom) + (stacked && base > 0 ? 1 : 0)}
                      width={barW}
                      height={Math.max(1, Math.abs(bottom - top) - (stacked && base > 0 ? 2 : 0))}
                      rx={2}
                      fill={COLORS[k % COLORS.length]}
                    />
                  );
                })}
                <text
                  x={PAD.left + i * band + band / 2}
                  y={H - 8}
                  fontSize="11"
                  textAnchor="middle"
                  fill="var(--muted)"
                >
                  {label}
                </text>
              </g>
            );
          })}
          <line x1={PAD.left} x2={W - PAD.right} y1={zero} y2={zero} stroke="var(--muted)" />
        </svg>
        {hover !== null && (
          <div
            role="tooltip"
            className="pointer-events-none absolute top-2 rounded border border-rule bg-sheet px-2 py-1 text-xs shadow"
            style={{ left: `${Math.min(70, ((PAD.left + hover * band) / W) * 100)}%` }}
          >
            <div className="font-medium">{labels[hover]}</div>
            {series.map((s) => (
              <div key={s.name}>
                {s.name}:{' '}
                {s.values[hover] === null || s.values[hover] === undefined
                  ? '—'
                  : format(s.values[hover]!)}
              </div>
            ))}
          </div>
        )}
      </div>
    </figure>
  );
}
